import React from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import { Fade } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { renderableType } from '~/src/Lib/PropTypes'
import SharedButton from '~/src/UI/Shared/Button'

import {
  getOnClick,
  isSelected,
  optionShape,
  valueShape,
} from './utils'

const DefaultButton = ({ active, option, ...props }) => (
  <SharedButton {...props} variant={active ? 'contained' : 'text'}>
    {option.label}
  </SharedButton>
)
DefaultButton.propTypes = {
  active: PropTypes.bool.isRequired,
  option: optionShape.isRequired,
}

const styles = theme => ({
  root: {
    display: 'flex',
    '& button:not(:first-child)': {
      marginLeft: theme.spacing(),
    },
    '&$vertical': {
      display: 'block',
      '& button, & [role=button], & [type=button]': {
        display: 'flex',
        '&:not(:first-child)': {
          marginLeft: 0,
          marginTop: theme.spacing()
        }
      }
    },
  },
  vertical: {},
})

const displayName = 'ToggleButtons'
const useStyles = makeStyles(styles, { name: displayName })

export const ToggleButtonsComponent = props => {
  const {
    Button,
    equals,
    options,
    onChange,
    selectAll,
    selectAllLabel,
    vertical,
    ...passthrough
  } = props
  const classes = useStyles(props)

  return (
    <div
      data-testid="responsive-chooser-toggle-buttons"
      {...passthrough}
      className={classNames(classes.root, vertical && classes.vertical)}
    >
      {selectAll && options.some(option => !isSelected(option, props)) ? (
        <Fade in>
          <Button
            key="select_all"
            active={false}
            option={{ label: selectAllLabel }}
            onClick={() => { onChange(options.map(option => option.value)) }}
          />
        </Fade>
      ) : null}
      {options.map(option => (
        <Button
          key={option.value}
          disabled={!!option.disabled}
          active={!!isSelected(option, props)}
          onClick={getOnClick(option, props)}
          option={option}
        />
      ))}
    </div>
  )
}

ToggleButtonsComponent.propTypes = {
  Button: renderableType,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(optionShape).isRequired,
  selectAll: PropTypes.bool,
  selectAllLabel: PropTypes.string,
  vertical: PropTypes.bool,
  value: valueShape,
  equals: PropTypes.func,
  onChange: PropTypes.func.isRequired,
}
ToggleButtonsComponent.defaultProps = {
  Button: React.memo(DefaultButton),
  value: null,
  multiple: true,
  selectAll: false,
  selectAllLabel: 'select all',
  vertical: false,
  equals: Object.is,
}
ToggleButtonsComponent.displayName = displayName

export default React.memo(ToggleButtonsComponent)
